import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SessionIdService {
  private sessionId: string | null = null;
  private correlationId: string | null = null;

  constructor() {
    this.initializeSessionId();
    this.initializeCorrelationId();
  }

  private generateSessionId(): string {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15) + 
                        Math.random().toString(36).substring(2, 15);
    return `${timestamp}-${randomString}`;
  }

  private initializeSessionId(): void {
    const storedSessionId = sessionStorage.getItem('app_session_id');
    
    if (storedSessionId) {
      this.sessionId = storedSessionId;
    } else {
      this.sessionId = this.generateSessionId();
      sessionStorage.setItem('app_session_id', this.sessionId);
    }
  }

  getSessionId(): string {
    if (!this.sessionId) {
      this.initializeSessionId();
    }
    return this.sessionId || '';
  }

  // journey_id: the correlationId generated by the backend at startSurvey and passed to the
  // frontend in the entry redirect URL as `?crltId=...`. We capture it once and echo it back as the
  // x-correlation-id header on every backend call so the whole respondent flow shares one
  // journey_id. If it is missing (e.g. a direct load), we fall back to the session id.
  private initializeCorrelationId(): void {
    let crltId: string | null = null;
    try {
      crltId = new URLSearchParams(window.location.search).get('crltId');
    } catch {
      crltId = null;
    }

    // A crltId in the entry URL means a fresh respondent journey - adopt and persist it (this also
    // correctly overrides a stale id from a previous survey in the same browser tab).
    if (crltId) {
      this.correlationId = crltId;
      sessionStorage.setItem('app_correlation_id', crltId);
      return;
    }

    // No crltId in the URL (in-app navigation): reuse the id captured for this journey.
    const storedCorrelationId = sessionStorage.getItem('app_correlation_id');
    if (storedCorrelationId) {
      this.correlationId = storedCorrelationId;
      return;
    }

    // Nothing to go on (e.g. a direct load): fall back to the session id so it is never empty.
    this.correlationId = this.getSessionId();
    sessionStorage.setItem('app_correlation_id', this.correlationId);
  }

  getCorrelationId(): string {
    if (!this.correlationId) {
      this.initializeCorrelationId();
    }
    return this.correlationId || '';
  }

  resetSessionId(): void {
    this.sessionId = this.generateSessionId();
    sessionStorage.setItem('app_session_id', this.sessionId);
  }
}

